<template>
    <form class="form-horizontal" method="POST" :action="formAction">
        <input type="hidden" name="_token" :value="formCsrf" />
        <input type="hidden" name="_method" :value="formMethod" />

        <x-form-group id="type" label="Tipe Form" :isRequired="true" :error="errors.type">
            <select id="type" name="type" class="form-control" v-model="type" required>
                <option value="cognito">Cognito Form</option>
                <option value="internal">Internal</option>
                <option value="other">Lain-Lain</option>
            </select>
        </x-form-group>

        <x-form-group id="label" label="Judul Form" :isRequired="true" :error="errors.label">
            <input type="text" class="form-control" id="label" name="label" v-model="label" required />
        </x-form-group>

        <x-form-group
            id="event_id"
            label="Event"
            :error="errors.event_id"
            :warning="eventWarning"
            helpText="Bila dikosongkan, secara otomatis form ini akan menjadi hidden form"
        >
            <v-select inputId="event_id" v-model="selectedEvent" :options="events" :reduce="(event) => event.id">
                <template v-slot:option="event">
                    {{ event.label }}
                    <small v-if="!!event.form" class="label bg-yellow pull-right">
                        Linked with {{ event.form.label }}
                    </small>
                </template>
            </v-select>
            <input type="hidden" name="event_id" :value="selectedEvent" />
        </x-form-group>

        <template v-if="type === 'internal'">
            <x-form-group id="validation_rule_ids" label="Validation Rules" :error="errors.validation_rule_ids">
                <x-input-group>
                    <x-ajax-dropdown
                        v-model="selectedValidationRules"
                        :multiple="true"
                        placeholder="Pick 1 or more validation rule(s)"
                        :allow-empty="true"
                        url="/api/validation-rules"
                        class="form-control"
                    />
                    <template #append-button>
                        <button
                            type="button"
                            class="btn btn-default"
                            @click.prevent="showValidationRuleModal"
                            title="Klik untuk menambahkan validation rule baru"
                        >
                            <i class="fa fa-plus" /> Create
                        </button>
                    </template>
                </x-input-group>
                <input
                    v-for="(rule, index) in selectedValidationRules"
                    :key="index"
                    type="hidden"
                    name="validation_rule_ids[]"
                    :value="rule.id"
                />
            </x-form-group>

            <x-form-group id="exception_lists" label="Exception List" :error="errors.exception_lists">
                <select id="exception_lists" name="exception_lists[]" class="form-control" multiple>
                    <option
                        v-for="(exceptionList, index) in exceptionLists"
                        :key="index"
                        selected="selected"
                        :value="exceptionList.id"
                    >
                        {{ exceptionList.name }}
                    </option>
                </select>
            </x-form-group>

            <x-form-group
                id="registration_start"
                label="Buka Pendaftaran"
                :error="errors.registration_start"
                helpText="Bila dikosongkan, tanggal buka pendaftaran akan mengikuti event"
            >
                <v-datepicker
                    v-model="registrationOpen"
                    name="registration_start"
                    id="registration_start"
                    format="YYYY-MM-DD H:i"
                    input-class="form-control"
                >
                </v-datepicker>
            </x-form-group>

            <x-form-group
                id="registration_end"
                label="Tutup Pendaftaran"
                :error="errors.registration_end"
                helpText="Bila dikosongkan, tanggal tutup pendaftaran akan mengikuti event"
            >
                <v-datepicker
                    v-model="registrationClose"
                    name="registration_end"
                    id="registration_end"
                    format="YYYY-MM-DD H:i"
                    input-class="form-control"
                >
                </v-datepicker>
            </x-form-group>

            <x-form-group
                id="allow_check_in"
                label="Aktifkan Check-In"
                :error="errors.allow_check_in"
                helpText="Bila di-centang, check-in akan diaktifkan untuk form ini"
            >
                <div class="checkbox">
                    <label>
                        <input v-model="allow_check_in" type="checkbox" value="1" name="allow_check_in" />
                        Aktifkan Check-In
                    </label>
                </div>
            </x-form-group>

            <x-form-group
                id="show_qr"
                label="QR Code"
                :error="errors.show_qr"
                helpText="Bila dicentang, QR code akan ditampilkan di halaman detail pendaftaran"
            >
                <div class="checkbox">
                    <label>
                        <input type="checkbox" name="show_qr" v-model="showQr" value="1" />
                        Tampilkan QR code peserta
                    </label>
                </div>
            </x-form-group>

            <x-form-group id="should_autofill" label="Autofill" :error="errors.should_autofill">
                <div class="checkbox">
                    <label>
                        <input type="checkbox" name="should_autofill" v-model="should_autofill" value="1" />
                        Aktifkan fitur autofill untuk form ini
                    </label>
                </div>
            </x-form-group>

            <x-form-group
                id="enable_user_data_update"
                label="Update Data Peserta"
                :error="errors.enable_user_data_update"
            >
                <div class="checkbox">
                    <label>
                        <input
                            type="checkbox"
                            name="enable_user_data_update"
                            v-model="enable_user_data_update"
                            value="1"
                        />
                        Bila aktif, data yang diisi di form akan secara otomatis meng-update data diri peserta
                    </label>
                </div>
            </x-form-group>

            <x-form-group
                id="allow_bulk_registration_when_logged_in"
                label="Batasi Bulk Registration"
                :error="errors.allow_bulk_registration_when_logged_in"
            >
                <div class="checkbox">
                    <label>
                        <input
                            type="checkbox"
                            name="allow_bulk_registration_when_logged_in"
                            v-model="allow_bulk_registration_when_logged_in"
                            value="1"
                        />
                        Bila aktif, fitur bulk registration hanya diaktifkan untuk user yang sudah login
                    </label>
                </div>
            </x-form-group>

            <x-form-group id="unique_email" label="Email" :error="errors.unique_email">
                <div class="checkbox">
                    <label>
                        <input type="checkbox" name="unique_email" v-model="unique_email" value="1" />
                        Bila dicentang, 1 email peserta hanya bisa digunakan sekali.
                    </label>
                </div>
            </x-form-group>

            <x-form-group id="static_placeholders" label="Custom Placeholders" :error="errors.static_placeholders">
                <div class="box box-primary collapsed-box">
                    <div class="box-header with-border">
                        <h3 class="box-title">Customize Placeholders</h3>
                        <div class="box-tools pull-right">
                            <button type="button" class="btn btn-box-tool" data-widget="collapse">
                                <i class="fa fa-plus"></i>
                            </button>
                        </div>
                    </div>
                    <div class="box-body">
                        <x-form-group id="static_placeholders.name" label="Nama">
                            <input
                                id="static_placeholders.name"
                                type="text"
                                name="static_placeholders[name]"
                                class="form-control"
                                placeholder="Nama lengkap sesuai KTP"
                                v-model="static_placeholders.name"
                            />
                        </x-form-group>
                        <x-form-group id="static_placeholders.email" label="Email">
                            <input
                                id="static_placeholders.email"
                                type="text"
                                name="static_placeholders[email]"
                                class="form-control"
                                v-model="static_placeholders.email"
                            />
                        </x-form-group>
                        <x-form-group id="static_placeholders.email_confirmation" label="Konfirmasi Email">
                            <input
                                id="static_placeholders.email_confirmation"
                                type="text"
                                name="static_placeholders[email_confirmation]"
                                class="form-control"
                                v-model="static_placeholders.email_confirmation"
                            />
                        </x-form-group>
                        <x-form-group id="static_placeholders.phone" label="Nomor Telpon">
                            <input
                                id="static_placeholders.phone"
                                type="text"
                                name="static_placeholders[phone]"
                                class="form-control"
                                v-model="static_placeholders.phone"
                            />
                        </x-form-group>
                        <x-form-group id="static_placeholders.dob" label="Tanggal Lahir">
                            <input
                                id="static_placeholders.dob"
                                type="text"
                                name="static_placeholders[dob]"
                                class="form-control"
                                placeholder="Gunakan format Tahun-bulan-tanggal. Contoh: 1990-01-22"
                                v-model="static_placeholders.dob"
                            />
                        </x-form-group>
                        <x-form-group id="static_placeholders.address" label="Alamat">
                            <input
                                id="static_placeholders.address"
                                type="text"
                                name="static_placeholders[address]"
                                class="form-control"
                                v-model="static_placeholders.address"
                            />
                        </x-form-group>
                    </div>
                </div>
            </x-form-group>

            <x-form-group
                id="note"
                label="Catatan"
                :error="errors.note"
                helpText="Catatan atau disclaimer untuk ditampilkan di form pendaftaran"
            >
                <textarea id="note" name="note" class="form-control" rows="6" v-model="note"></textarea>
            </x-form-group>

            <x-form-group :id="`capacity`" label="Kapasitas" helpText="Kosongkan bila tidak ada batasan kapasitas.">
                <input type="number" class="form-control" :id="`capacity`" :name="`capacity`" v-model="capacity" />
            </x-form-group>

            <x-form-group
                :id="`external_link_label`"
                label="Label Tombol Meeting Online"
                helpText='Bila dikosongkan, secara default akan tampil "Masuk ke Online Meeting"'
            >
                <input
                    type="text"
                    class="form-control"
                    :id="`external_link_label`"
                    :name="`external_link_label`"
                    v-model="external_link_label"
                />
            </x-form-group>
            <x-form-group :id="`external_link`" label="Link Meeting Online">
                <input
                    type="text"
                    class="form-control"
                    :id="`external_link`"
                    :name="`external_link`"
                    v-model="external_link"
                    placeholder="Isi dengan link Zoom, Google Meet, dll."
                />
            </x-form-group>
            <x-form-group :id="`subscriber_note`" label="Catatan untuk Peserta Terdaftar">
                <textarea
                    class="form-control"
                    :id="`subscriber_note`"
                    :name="`subscriber_note`"
                    v-model="subscriber_note"
                    placeholder="Catatan untuk Peserta yang sudah berhasil mendaftar"
                ></textarea>
            </x-form-group>

            <x-form-group label="Custom Fields" :error="errors.fields">
                <x-input-custom-form-fields :errors="errors" v-if="isReady" v-model="fields" />
            </x-form-group>

            <event-form-validation-modal id="event-form-validation-modal" @created="addValidationRule" />
        </template>

        <template v-else>
            <x-form-group id="link" label="Link" :isRequired="true" :error="errors.external_link">
                <input
                    type="text"
                    class="form-control"
                    id="external_link"
                    name="external_link"
                    v-model="external_link"
                    required
                />
            </x-form-group>
        </template>

        <template v-if="type === 'cognito'">
            <x-form-group label="Field Mapping">
                <p>
                    Silakan sesuaikan <b>Internal Field Name</b> di Cognito dengan tabel berikut. Klik
                    <a href="https://www.cognitoforms.com/support/49/calculations/developer-mode" target="_blank"
                        >di sini</a
                    >
                    untuk informasi lebih lanjut tentang cara setting
                    <b>Internal Field Name</b> di Cognito Forms.
                </p>
                <x-table :headers="['Field', 'Keterangan', 'Wajib?']">
                    <tr>
                        <td><code>name</code></td>
                        <td>Nama pendaftar</td>
                        <td>Ya</td>
                    </tr>
                    <tr>
                        <td><code>email</code></td>
                        <td>Email pendaftar</td>
                        <td>Ya</td>
                    </tr>
                    <tr>
                        <td><code>dob</code></td>
                        <td>Tanggal lahir</td>
                        <td>Tidak</td>
                    </tr>
                    <tr>
                        <td><code>phone</code></td>
                        <td>No HP</td>
                        <td>Tidak</td>
                    </tr>
                    <tr>
                        <td><code>gender</code></td>
                        <td>
                            Jenis Kelamin boleh dikosongkan, tapi bila tidak kosong harus diisi dengan
                            <code>male</code>, <code>female</code>, <code>Laki-laki</code>, <code>Pria</code>,
                            <code>Wanita</code>, atau <code>Perempuan</code>. Pengecekan bersifat case-insensitive.
                        </td>
                        <td>Tidak</td>
                    </tr>
                    <tr>
                        <td><code>address</code></td>
                        <td>Alamat</td>
                        <td>Tidak</td>
                    </tr>
                </x-table>
            </x-form-group>
            <x-form-group label="Submit Webhook URL">
                <p v-if="id">
                    Copy & paste link berikut ke <b>Submit Entry Endpoint</b> di Cognito Form bersangkutan. Klik
                    <a
                        href="https://www.cognitoforms.com/support/66/data-integration/webhooks#viewing-a-post-from-cognito-forms"
                        target="_blank"
                        >di sini</a
                    >
                    untuk informasi lebih lanjut tentang cara setting
                    <b>Submit Entry Endpoint</b> di Cognito Forms.
                </p>
                <span v-else>
                    Silakan simpan form ini terlebih dahulu untuk mendapatkan
                    <b>Webhook URL</b> yang akan digunakan di Cognito Forms.
                </span>
                <input v-if="id" type="text" class="form-control" readonly :value="submit_webhook_url" />
            </x-form-group>
            <x-form-group label="Update Webhook URL">
                <p v-if="id">
                    Copy & paste link berikut ke <b>Update Entry Endpoint</b> di Cognito Form bersangkutan. Klik
                    <a
                        href="https://www.cognitoforms.com/support/66/data-integration/webhooks#viewing-a-post-from-cognito-forms"
                        target="_blank"
                        >di sini</a
                    >
                    untuk informasi lebih lanjut tentang cara setting
                    <b>Update Entry Endpoint</b> di Cognito Forms.
                </p>
                <span v-else>
                    Silakan simpan form ini terlebih dahulu untuk mendapatkan
                    <b>Webhook URL</b> yang akan digunakan di Cognito Forms.
                </span>
                <input v-if="id" type="text" class="form-control" readonly :value="update_webhook_url" />
            </x-form-group>
            <x-form-group label="Delete Webhook URL">
                <p v-if="id">
                    Copy & paste link berikut ke <b>Delete Entry Endpoint</b> di Cognito Form bersangkutan. Klik
                    <a
                        href="https://www.cognitoforms.com/support/66/data-integration/webhooks#viewing-a-post-from-cognito-forms"
                        target="_blank"
                        >di sini</a
                    >
                    untuk informasi lebih lanjut tentang cara setting
                    <b>Delete Entry Endpoint</b> di Cognito Forms.
                </p>
                <span v-else>
                    Silakan simpan form ini terlebih dahulu untuk mendapatkan
                    <b>Webhook URL</b> yang akan digunakan di Cognito Forms.
                </span>
                <input v-if="id" type="text" class="form-control" readonly :value="delete_webhook_url" />
            </x-form-group>
            <x-form-group label="Registration Redirect URL">
                <p v-if="id">
                    Copy & paste link berikut ke <b>Confirmation Redirect URL</b> di Cognito Form bersangkutan. Klik
                    <a
                        href="https://www.cognitoforms.com/support/14/building-forms/create-customized-confirmation-pages#redirect-url"
                        target="_blank"
                    >di sini</a
                    >
                    untuk informasi lebih lanjut tentang cara setting
                    <b>Confirmation Redirect URL</b> di Cognito Forms.
                </p>
                <span v-else>
                    Silakan simpan form ini terlebih dahulu untuk mendapatkan
                    <b>Registration Redirect URL</b> yang akan digunakan di Cognito Forms.
                </span>
                <input v-if="id" type="text" class="form-control" readonly :value="registration_redirect_url" />
            </x-form-group>
            <x-form-group
                id="allow_check_in"
                label="Aktifkan Check-In"
                :error="errors.allow_check_in"
                helpText="Bila di-centang, check-in akan diaktifkan untuk form ini"
            >
                <div class="checkbox">
                    <label>
                        <input v-model="allow_check_in" type="checkbox" value="1" name="allow_check_in" />
                        Aktifkan Check-In
                    </label>
                </div>
            </x-form-group>

            <x-form-group
                id="show_qr"
                label="QR Code"
                :error="errors.show_qr"
                helpText="Bila dicentang, QR code akan ditampilkan di halaman detail pendaftaran"
            >
                <div class="checkbox">
                    <label>
                        <input type="checkbox" name="show_qr" v-model="showQr" value="1" />
                        Tampilkan QR code peserta
                    </label>
                </div>
            </x-form-group>
            <x-form-group :id="`subscriber_note`" label="Catatan untuk Peserta Terdaftar">
                <textarea
                    class="form-control"
                    :id="`subscriber_note`"
                    :name="`subscriber_note`"
                    v-model="subscriber_note"
                    placeholder="Catatan untuk Peserta yang sudah berhasil mendaftar"
                ></textarea>
            </x-form-group>
        </template>

        <div class="form-group">
            <div class="col-sm-offset-2 col-sm-10">
                <button type="submit" class="btn btn-primary">Submit</button>
            </div>
        </div>
    </form>
</template>

<script>
import datetime from "vuejs-datetimepicker";
import XAjaxDropdown from "../../components/XAjaxDropdown.vue";
import XFormGroup from "../../components/XFormGroup.vue";
import XInputCustomFormFields from "../../components/XInputCustomFormFields.vue";
import XInputGroup from "../../components/XInputGroup.vue";
import XTable from "../../components/XTable.vue";
import FormErrorMixin from "../../mixins/FormErrorMixin";
import EventFormValidationModal from "./EventFormValidationModal.vue";

export default {
    components: {
        EventFormValidationModal,
        "v-datepicker": datetime,
        XFormGroup,
        XInputGroup,
        XAjaxDropdown,
        XInputCustomFormFields,
        XTable,
    },
    computed: {
        appUrl() {
            return $('meta[name="app-url"]').attr("content");
        },
        eventWarning() {
            const targetEvent = this.events.find((event) => {
                return event.id == this.selectedEvent;
            });

            if (!targetEvent) {
                return null;
            }

            if (!targetEvent.form) {
                return null;
            }

            if (targetEvent.form.id == this.eventFormId) {
                return null;
            }

            return `Link antara event dengan ${targetEvent.form.label} akan terputus`;
        },
    },
    data() {
        return {
            allow_bulk_registration_when_logged_in: true,
            allow_check_in: true,
            capacity: null,
            enable_user_data_update: true,
            errors: {},
            eventFormId: null,
            events: [],
            exceptionLists: [],
            external_link: null,
            external_link_label: null,
            fields: [],
            formAction: null,
            formCsrf: null,
            formMethod: null,
            id: null,
            isReady: false,
            label: null,
            note: null,
            registrationClose: null,
            registrationOpen: null,
            selectedEvent: null,
            selectedValidationRules: [],
            should_autofill: true,
            showQr: true,
            static_placeholders: {
                address: null,
                dob: null,
                email: null,
                email_confirmation: null,
                name: null,
                phone: null,
            },
            subscriber_note: null,
            type: "internal",
            unique_email: true,
            submit_webhook_url: null,
            delete_webhook_url: null,
            registration_redirect_url: null,
        };
    },
    mounted() {
        this._loadFormMeta();
        this._loadEvents();
        this._loadDefaults();
        this._loadFormErrors();
        this.initSelect2();
    },
    watch: {
        type(newType, oldType) {
            if (newType === "internal") {
                this.$nextTick(() => {
                    this.initSelect2();
                });
            } else if (oldType === "internal") {
                this.destroySelect2();
            }
        },
    },
    methods: {
        addValidationRule(rule) {
            this.selectedValidationRules.push(rule);
        },
        removeField(targetIndex) {
            this.fields = this.fields.filter((field, index) => {
                return index != targetIndex;
            });
        },
        showValidationRuleModal() {
            $("#event-form-validation-modal").modal("show");
        },
        initSelect2() {
            if (this.type !== "internal") return;

            const element = "#exception_lists";

            // Destroy existing Select2 instance if it exists
            if ($(element).hasClass("select2-hidden-accessible")) {
                $(element).select2("destroy");
            }

            $(element).select2({
                placeholder: "Pilih Exception List",
                minimumInputLength: 0,
                allowClear: true,
                theme: "classic",
                debug: true,
                ajax: {
                    delay: 500,
                    url: `/admin/exception-lists`,
                    dataType: "json",
                    data: function (params) {
                        return {
                            term: params.term,
                            page: params.page || 1,
                        };
                    },
                    processResults: function (response) {
                        return {
                            results: response.data.map(function (item) {
                                return {
                                    id: item.id,
                                    text: item.name,
                                };
                            }),
                            pagination: {
                                more: !!response.links.next,
                            },
                        };
                    },
                },
            });
        },
        destroySelect2() {
            const element = "#exception_lists";
            if ($(element).hasClass("select2-hidden-accessible")) {
                $(element).select2("destroy");
            }
        },
        _loadFormMeta() {
            this.formAction = $("#action").val();
            this.formMethod = $("#method").val();
            this.formCsrf = $('meta[name="csrf-token"]').attr("content");
        },
        _loadFormErrors() {
            let errors = JSON.parse($("#errors").val());

            for (let key in errors) {
                if (typeof errors[key] === "undefined") {
                    continue;
                }
                errors[key] = errors[key].join(", ");
            }

            this.errors = Array.isArray(errors) ? {} : errors;
        },
        _loadEvents() {
            this.events = JSON.parse($("#events").val());
        },
        _loadDefaults() {
            let old = JSON.parse($("#old").val());

            if (old.length === 0) {
                this.isReady = true;
                return;
            }

            this.allow_bulk_registration_when_logged_in = old.allow_bulk_registration_when_logged_in;
            this.allow_check_in = old.allow_check_in;
            this.capacity = old.capacity;
            this.enable_user_data_update = old.enable_user_data_update;
            this.exceptionLists = _.get(old, "exception_lists", []);
            this.external_link = old.external_link;
            this.external_link_label = old.external_link_label;
            this.id = old.id || null;
            this.label = old.label;
            this.note = old.note;
            this.registrationClose = old.registration_end;
            this.registrationOpen = old.registration_start;
            this.selectedEvent = old.event_id !== null ? parseInt(old.event_id) : old.event_id;
            this.selectedValidationRules = old.validation_rules;
            this.should_autofill = old.should_autofill;
            this.showQr = old.show_qr;
            this.static_placeholders = {
                ...this.static_placeholders,
                ...old.static_placeholders,
            };
            this.subscriber_note = old.subscriber_note;
            this.type = old.type || "internal";
            this.unique_email = old.unique_email;
            this.submit_webhook_url = old.submit_webhook_url || null;
            this.update_webhook_url = old.update_webhook_url || null;
            this.delete_webhook_url = old.delete_webhook_url || null;
            this.registration_redirect_url = old.registration_redirect_url || null;

            if (typeof old.id !== "undefined") {
                this.eventFormId = old.id;
            }

            if (typeof old.fields !== "undefined") {
                this.fields = old.fields.map((field) => {
                    return {
                        ...field,
                        disabled: $("#has_subscriber").val() == 1,
                    };
                });
            }

            this.isReady = true;
        },
    },
    mixins: [FormErrorMixin],
    props: {
        limitedCapability: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
};
</script>
